
import React, { useState, useCallback, useRef, useEffect } from 'react';
import type { BaseProblem, ManualCrop } from '../types';
import toast from 'react-hot-toast';
import ManualCropView from './ManualCropView';

interface AddBaseProblemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddMultipleBaseProblems: (problems: Omit<BaseProblem, 'id' | 'duplicateOf' | 'deletedAt'>[]) => void;
  onQueueImport: (files: File[], type: 'single' | 'dual', folderName: string, manualCrops?: ManualCrop[]) => Promise<void>;
  allFolderPaths: string[];
  onAddFolder: (path: string) => Promise<void>;
}

const AddBaseProblemModal: React.FC<AddBaseProblemModalProps> = ({ isOpen, onClose, onQueueImport, allFolderPaths, onAddFolder }) => {
  const [mode, setMode] = useState<'single' | 'dual'>('single');
  
  // Single Mode State
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  
  // Dual Mode State
  const [problemFile, setProblemFile] = useState<File | null>(null);
  const [explanationFile, setExplanationFile] = useState<File | null>(null);
  
  // Folder Selection State
  const [folderMode, setFolderMode] = useState<'new' | 'existing'>('new');
  const [newFolderName, setNewFolderName] = useState('');
  const [selectedExistingFolder, setSelectedExistingFolder] = useState('');
  const [useFilenameAsFolder, setUseFilenameAsFolder] = useState(false);

  const [error, setError] = useState('');
  const [isQueuing, setIsQueuing] = useState(false);
  
  // Manual Crop State
  const [isManualMode, setIsManualMode] = useState(false);
  const [showCropView, setShowCropView] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const problemInputRef = useRef<HTMLInputElement>(null);
  const explanationInputRef = useRef<HTMLInputElement>(null);

  const resetState = useCallback(() => {
    setSelectedFiles([]);
    setProblemFile(null);
    setExplanationFile(null);
    setError('');
    setNewFolderName('');
    setSelectedExistingFolder('');
    setFolderMode('new');
    setUseFilenameAsFolder(false);
    setIsQueuing(false);
    setIsManualMode(false);
    setShowCropView(false);
    if(fileInputRef.current) fileInputRef.current.value = '';
    if(problemInputRef.current) problemInputRef.current.value = '';
    if(explanationInputRef.current) explanationInputRef.current.value = '';
  }, []);

  const handleClose = useCallback(() => {
      resetState();
      onClose();
  }, [resetState, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, handleClose]);

  // Set default existing folder if available
  useEffect(() => {
      if (isOpen && allFolderPaths.length > 0 && !selectedExistingFolder) {
          setSelectedExistingFolder(allFolderPaths[0]);
      }
  }, [isOpen, allFolderPaths, selectedExistingFolder]);

  const handleSingleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
        const newFiles = Array.from(event.target.files);
        setSelectedFiles(prev => [...prev, ...newFiles]);
        setError('');
        // Reset input so same file can be selected again if needed (though unlikely with multiple)
        event.target.value = '';
    }
  };

  const removeFile = (index: number) => {
      setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDualFileChange = (event: React.ChangeEvent<HTMLInputElement>, setFile: (f: File | null) => void) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError('');
    }
  };
  
  const handleStart = async () => {
      if (mode === 'single' && selectedFiles.length === 0) {
          setError("파일을 하나 이상 선택해주세요.");
          return;
      }
      if (mode === 'dual' && (!problemFile || !explanationFile)) {
          setError("문제 파일과 해설 파일을 모두 선택해주세요.");
          return;
      }
      
      // Check PDF requirement for Manual Mode
      if (isManualMode && mode === 'single') {
          if (selectedFiles.length > 1) {
              setError("직접 선택 기능은 한 번에 하나의 파일만 가능합니다.");
              return;
          }
          if (selectedFiles[0].type !== 'application/pdf') {
              setError("직접 선택 기능은 PDF 파일만 지원합니다.");
              return;
          }
          setShowCropView(true);
          return;
      }
      
      await executeQueue(undefined);
  };
  
  const executeQueue = async (manualCrops?: ManualCrop[]) => {
      let finalFolderName = '';
      
      if (!useFilenameAsFolder) {
          if (folderMode === 'new') {
              if (!newFolderName.trim()) {
                  setError("새 폴더 이름을 입력해주세요.");
                  return;
              }
              finalFolderName = newFolderName.trim();
              // Immediately register the new folder to show it in the sidebar
              onAddFolder(finalFolderName);
          } else {
              if (!selectedExistingFolder) {
                  setError("기존 폴더를 선택해주세요.");
                  return;
              }
              finalFolderName = selectedExistingFolder;
          }
      }
      
      setIsQueuing(true);
      try {
          if (mode === 'single') {
              // Batch process for multiple files
              // We create one job per file to allow better tracking and partial failures
              const queuePromises = selectedFiles.map(async file => {
                  let targetFolder = finalFolderName;
                  if (useFilenameAsFolder) {
                      // Use filename (without extension) as folder
                      targetFolder = file.name.replace(/\.[^/.]+$/, "").trim();
                      await onAddFolder(targetFolder);
                  }
                  return onQueueImport([file], 'single', targetFolder, undefined);
              });
              
              // If manual crops exist (implies single file), use them
              if (manualCrops && selectedFiles.length === 1) {
                  let targetFolder = finalFolderName;
                  if (useFilenameAsFolder) {
                      targetFolder = selectedFiles[0].name.replace(/\.[^/.]+$/, "").trim();
                      await onAddFolder(targetFolder);
                  }
                  await onQueueImport([selectedFiles[0]], 'single', targetFolder, manualCrops);
              } else {
                  await Promise.all(queuePromises);
              }
              
              toast.success(`${selectedFiles.length}개의 작업이 요청되었습니다.`, {
                  duration: 4000,
                  icon: '🚀',
                  style: { borderRadius: '10px', background: '#333', color: '#fff' },
              });
          } else {
              // Dual mode (Single job with 2 files)
              await onQueueImport([problemFile!, explanationFile!], 'dual', finalFolderName, undefined);
              toast.success('작업 관리자에서 확인하세요', {
                  duration: 4000,
                  icon: '🚀',
                  style: { borderRadius: '10px', background: '#333', color: '#fff' },
              });
          }
          handleClose();
      } catch (e) {
          console.error(e);
          setError("작업 요청에 실패했습니다.");
          setIsQueuing(false);
      }
  };

  if (!isOpen) return null;
  
  if (showCropView && selectedFiles.length === 1) {
      return (
          <ManualCropView 
            file={selectedFiles[0]} 
            onFinish={(crops) => executeQueue(crops)} 
            onCancel={() => setShowCropView(false)} 
          />
      );
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex justify-center items-center z-50 p-4" onClick={handleClose}>
      <div className="bg-[var(--color-bg-glass)] border border-[var(--color-border-glass)] rounded-lg shadow-xl w-full max-w-2xl flex flex-col p-6 max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6 flex-shrink-0">
            <h2 className="text-2xl font-bold text-[var(--color-accent)]">새 기반문제 추가</h2>
            <button onClick={handleClose} className="text-gray-400 hover:text-white text-3xl">&times;</button>
        </div>
        
        <div className="space-y-6 overflow-y-auto pr-2">
            {/* File Mode Tabs */}
            <div className="flex border-b border-[var(--color-border-glass)]">
                <button 
                    onClick={() => { setMode('single'); setIsManualMode(false); }}
                    className={`px-4 py-2 font-semibold text-sm border-b-2 transition-colors ${mode === 'single' ? 'border-[var(--color-accent)] text-[var(--color-accent)]' : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'}`}
                >
                    자동 (다중 파일)
                </button>
                <button 
                    onClick={() => { setMode('dual'); setIsManualMode(false); }}
                    className={`px-4 py-2 font-semibold text-sm border-b-2 transition-colors ${mode === 'dual' ? 'border-[var(--color-accent)] text-[var(--color-accent)]' : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'}`}
                >
                    문제/해설 분리
                </button>
            </div>
            
            <div className="space-y-4">
                {mode === 'single' ? (
                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <p className="text-sm text-[var(--color-text-secondary)]">문제와 해설이 같이 있는 파일을 선택하세요. (여러 개 가능)</p>
                            <div className="flex items-center gap-2">
                                <label className={`text-sm text-[var(--color-text-primary)] font-bold flex items-center gap-2 cursor-pointer ${selectedFiles.length > 1 ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                    <input 
                                        type="checkbox" 
                                        checked={isManualMode} 
                                        onChange={(e) => setIsManualMode(e.target.checked)} 
                                        disabled={selectedFiles.length > 1}
                                        className="accent-[var(--color-accent)]" 
                                    />
                                    직접 영역 선택 (PDF 1개만)
                                </label>
                            </div>
                        </div>
                        
                        {/* File Drop Zone */}
                        <div className="flex justify-center px-6 pt-5 pb-6 border-2 border-[var(--color-border)] border-dashed rounded-md hover:border-[var(--color-accent)] transition-colors group">
                            <div className="space-y-1 text-center">
                                <svg className="mx-auto h-12 w-12 text-[var(--color-text-tertiary)] group-hover:text-[var(--color-accent)] transition-colors" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <div className="flex justify-center text-sm">
                                    <label htmlFor="file-upload-multi" className="relative cursor-pointer rounded-md font-medium text-[var(--color-accent)] hover:text-[var(--color-accent-hover)]">
                                        <span>파일 선택 (클릭)</span>
                                        <input 
                                            ref={fileInputRef} 
                                            id="file-upload-multi" 
                                            name="file-upload" 
                                            type="file" 
                                            className="sr-only" 
                                            multiple 
                                            accept={isManualMode ? ".pdf" : "image/*,.pdf"} 
                                            onChange={handleSingleFileChange} 
                                        />
                                    </label>
                                </div>
                                <p className="text-xs text-[var(--color-text-tertiary)]">{isManualMode ? "PDF 파일만 지원" : "PDF, JPG, PNG 지원"}</p>
                            </div>
                        </div>

                        {/* Selected Files List */}
                        {selectedFiles.length > 0 && (
                            <div className="mt-4 bg-black/20 rounded-lg border border-[var(--color-border-glass)] max-h-40 overflow-y-auto">
                                {selectedFiles.map((file, index) => (
                                    <div key={index} className="flex items-center justify-between p-2 text-sm border-b border-[var(--color-border-glass)] last:border-none hover:bg-white/5">
                                        <div className="flex items-center gap-2 truncate">
                                            <span className="text-[var(--color-text-tertiary)]">{index + 1}.</span>
                                            <span className="text-[var(--color-text-primary)] truncate">{file.name}</span>
                                            <span className="text-xs text-[var(--color-text-tertiary)]">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                                        </div>
                                        <button 
                                            onClick={() => removeFile(index)} 
                                            className="text-gray-500 hover:text-red-400 p-1 rounded-full hover:bg-red-500/10 transition-colors"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                            </svg>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        {selectedFiles.length > 1 && isManualMode && (
                            <p className="text-xs text-amber-400 mt-2">* 여러 파일이 선택되어 '직접 영역 선택' 모드가 해제되었습니다.</p>
                        )}
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-4">
                         <div>
                            <label className="block text-sm font-bold text-[var(--color-text-primary)] mb-2">문제 파일</label>
                            <div className="flex justify-center px-4 pt-5 pb-6 border-2 border-[var(--color-border)] border-dashed rounded-md hover:border-[var(--color-accent)] transition-colors">
                                <div className="space-y-1 text-center">
                                    <div className="flex justify-center text-sm">
                                        <label htmlFor="file-upload-p" className="relative cursor-pointer text-[var(--color-accent)] hover:underline">
                                        <span>선택</span>
                                        <input ref={problemInputRef} id="file-upload-p" type="file" className="sr-only" accept="image/*,.pdf" onChange={(e) => handleDualFileChange(e, setProblemFile)} />
                                        </label>
                                    </div>
                                </div>
                            </div>
                            {problemFile && <p className="text-xs text-[var(--color-text-secondary)] mt-1 truncate font-mono bg-black/20 p-1 rounded">{problemFile.name}</p>}
                         </div>
                         <div>
                            <label className="block text-sm font-bold text-[var(--color-text-primary)] mb-2">해설 파일</label>
                            <div className="flex justify-center px-4 pt-5 pb-6 border-2 border-[var(--color-border)] border-dashed rounded-md hover:border-[var(--color-accent)] transition-colors">
                                <div className="space-y-1 text-center">
                                    <div className="flex justify-center text-sm">
                                        <label htmlFor="file-upload-e" className="relative cursor-pointer text-[var(--color-accent)] hover:underline">
                                        <span>선택</span>
                                        <input ref={explanationInputRef} id="file-upload-e" type="file" className="sr-only" accept="image/*,.pdf" onChange={(e) => handleDualFileChange(e, setExplanationFile)} />
                                        </label>
                                    </div>
                                </div>
                            </div>
                             {explanationFile && <p className="text-xs text-[var(--color-text-secondary)] mt-1 truncate font-mono bg-black/20 p-1 rounded">{explanationFile.name}</p>}
                         </div>
                    </div>
                )}
                
                <div>
                    <label className="block text-sm font-bold text-[var(--color-text-secondary)] mb-2">저장할 위치</label>
                    
                    {mode === 'single' && (
                        <div className="mb-3">
                            <label className="flex items-center gap-2 cursor-pointer text-sm text-[var(--color-text-primary)]">
                                <input 
                                    type="checkbox" 
                                    checked={useFilenameAsFolder} 
                                    onChange={(e) => setUseFilenameAsFolder(e.target.checked)} 
                                    className="accent-[var(--color-accent)] w-4 h-4" 
                                />
                                각 파일명으로 폴더 자동 생성
                            </label>
                            {useFilenameAsFolder && (
                                <p className="text-xs text-[var(--color-text-tertiary)] mt-1 ml-6">
                                    선택된 각 파일의 이름으로 폴더를 만들고 저장합니다.
                                </p>
                            )}
                        </div>
                    )}

                    {!useFilenameAsFolder && (
                        <>
                            <div className="flex gap-2 mb-3">
                                <button
                                    onClick={() => setFolderMode('new')}
                                    className={`flex-1 py-2 px-3 rounded-md text-xs font-bold border transition-colors ${
                                        folderMode === 'new' 
                                        ? 'bg-[var(--color-accent)] border-[var(--color-accent)] text-white' 
                                        : 'bg-transparent border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-white/5'
                                    }`}
                                >
                                    새 폴더 만들기
                                </button>
                                <button
                                    onClick={() => setFolderMode('existing')}
                                    disabled={allFolderPaths.length === 0}
                                    className={`flex-1 py-2 px-3 rounded-md text-xs font-bold border transition-colors ${
                                        folderMode === 'existing' 
                                        ? 'bg-[var(--color-accent)] border-[var(--color-accent)] text-white' 
                                        : 'bg-transparent border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed'
                                    }`}
                                >
                                    기존 폴더에서 선택
                                </button>
                            </div>

                            {folderMode === 'new' ? (
                                <input 
                                    type="text" 
                                    value={newFolderName}
                                    onChange={(e) => setNewFolderName(e.target.value)}
                                    placeholder="새 폴더 이름을 작성해주세요"
                                    className="w-full bg-black/20 border border-[var(--color-border-glass)] rounded-md py-2 px-3 text-[var(--color-text-primary)] placeholder-[var(--color-text-tertiary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
                                />
                            ) : (
                                <select 
                                    value={selectedExistingFolder} 
                                    onChange={(e) => setSelectedExistingFolder(e.target.value)}
                                    className="w-full bg-black/20 border border-[var(--color-border-glass)] rounded-md py-2 px-3 text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
                                >
                                    {allFolderPaths.map(path => (
                                        <option key={path} value={path}>{path}</option>
                                    ))}
                                </select>
                            )}
                        </>
                    )}
                </div>
                
                {error && <div className="bg-red-900/50 p-3 rounded-md text-sm text-red-300 border border-red-500/30 text-center animate-pulse">{error}</div>}
            </div>
            
            <div className="flex justify-end pt-4 border-t border-[var(--color-border-glass)]">
                 <button onClick={handleStart} disabled={isQueuing} className="bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] text-[var(--color-accent-text)] font-bold py-2 px-6 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed">
                    {isQueuing 
                        ? '요청 중...' 
                        : (isManualMode && selectedFiles.length === 1 
                            ? '영역 선택 시작' 
                            : mode === 'single' 
                                ? `${selectedFiles.length}개 파일 일괄 작업 시작` 
                                : '작업 시작 (백그라운드)')}
                 </button>
            </div>
        </div>
      </div>
    </div>
  );
};

export default AddBaseProblemModal;
